import React, { useEffect, useMemo, useState } from "react";
import "./admin-user.css";
import { FaSearch } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import { FaEye } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import ConfirmModal from "../../components/modal/confirm-modal";
import { deleteUserForAdmin, getAllUsersForAdmin } from "../../api/user.api";
import { useUser } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/auth";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;

export const AdminUser = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);
  const [sortByBalance, setSortByBalance] = useState("false");

  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    document.title = "Admin - Người dùng";
  }, []);

  useEffect(() => {
    fetchUserList();
  }, [currentPage, debouncedSearchInput, sortByBalance]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const summary = useMemo(() => {
    return users.reduce(
      (acc, item) => {
        if (item.isBanned) acc.banned += 1;
        else acc.active += 1;

        if (item.role === "admin" || item.role === "editor" || item.role === "supporter") {
          acc.staff += 1;
        }
        return acc;
      },
      { active: 0, banned: 0, staff: 0 }
    );
  }, [users]);

  const fetchUserList = async (showLoader = true) => {
    if (showLoader) setIsFetching(true);
    try {
      const res = await getAllUsersForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim(),
        sortByBalance === "true"
      );

      if (res.success) {
        setUsers(res.data.users || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalUsers(res.data.total || 0);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      if (showLoader) setIsFetching(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleDeleteUser = async () => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setIsModalOpen(false);
    try {
      const res = await deleteUserForAdmin(selectedIdToDelete);
      if (res.success) {
        showToast("Xóa người dùng thành công", "success");
        await fetchUserList(false);
        setSelectedIdToDelete("");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="admin-user-page">
      <ToastNotification />
      <div className="admin-user-shell">
        <div className="admin-user-header">
          <div>
            <p className="admin-user-breadcrumb">Quản trị / Người dùng</p>
            <h1 className="admin-user-title">Danh sách người dùng</h1>
          </div>
          <div className="admin-user-kpi">
            <div className="admin-user-kpi-item">
              <span>Tổng người dùng</span>
              <strong>{totalUsers}</strong>
            </div>
            <div className="admin-user-kpi-item">
              <span>Trang hiện tại</span>
              <strong>{users.length}</strong>
            </div>
            <div className="admin-user-kpi-item">
              <span>Nhân sự hệ thống</span>
              <strong>{summary.staff}</strong>
            </div>
          </div>
        </div>

        <div className="admin-user-toolbar">
          <div className="admin-user-search-wrap">
            <FaSearch className="admin-user-search-icon" />
            <input
              type="text"
              placeholder="Tìm kiếm theo ID hoặc username"
              className="admin-user-search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <select
            className="admin-user-filter"
            value={sortByBalance}
            onChange={(e) => {
              setSortByBalance(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="false">Mặc định</option>
            <option value="true">Ưu tiên số dư cao</option>
          </select>
        </div>

        {isFetching && users.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <div className="admin-user-table-wrap">
              <table className="admin-user-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>ID</th>
                    <th>Tên người dùng</th>
                    <th>Vai trò</th>
                    <th>Số dư</th>
                    <th>Trạng thái</th>
                    <th>Tạo lúc</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((item, index) => (
                      <tr key={item._id}>
                        <td className="admin-user-cell-muted">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td className="admin-user-cell-code">{item._id}</td>
                        <td>
                          <button
                            type="button"
                            disabled={item._id === user?._id}
                            onClick={() => navigate(`/super-admin/users/view/${item._id}`)}
                            className="admin-user-name-link"
                          >
                            {item.username}
                          </button>
                        </td>
                        <td>
                          <span className={`admin-user-role admin-user-role-${item.role}`}>{item.role}</span>
                        </td>
                        <td className="admin-user-cell-balance">{item.balance?.toLocaleString("vi-VN") || 0}đ</td>
                        <td>
                          <span className={`admin-user-status ${item.isBanned ? "admin-user-status-banned" : "admin-user-status-active"}`}>
                            {item.isBanned ? "Bị chặn" : "Hoạt động"}
                          </span>
                        </td>
                        <td className="admin-user-cell-muted">{new Date(item.createdAt).toLocaleString("vi-VN")}</td>
                        <td>
                          <div className="admin-user-actions">
                            {item._id !== user?._id && (
                              <>
                                <button
                                  onClick={() => navigate(`/super-admin/users/view/${item._id}`)}
                                  className="admin-user-action admin-user-action-view"
                                  disabled={isActionLoading}
                                  title="Xem chi tiết"
                                >
                                  <FaEye />
                                </button>
                                {hasPermission(user?.role, "edit:user") && (
                                  <button
                                    onClick={() => navigate(`/super-admin/users/edit/${item._id}`)}
                                    className="admin-user-action admin-user-action-edit"
                                    disabled={isActionLoading}
                                    title="Chỉnh sửa"
                                  >
                                    <FaEdit />
                                  </button>
                                )}
                                {hasPermission(user?.role, "delete:user") && (
                                  <button
                                    onClick={() => {
                                      setIsModalOpen(true);
                                      setSelectedIdToDelete(item._id);
                                    }}
                                    className="admin-user-action admin-user-action-delete"
                                    disabled={isActionLoading}
                                    title="Xóa người dùng"
                                  >
                                    <MdDelete />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8}>
                        <p className="admin-user-empty">Không có người dùng phù hợp bộ lọc.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
              />
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={handleDeleteUser}
        onClose={() => setIsModalOpen(false)}
        message={"Hãy chắc chắn rằng bạn đang xóa người dùng này, vì toàn bộ dữ liệu liên quan sẽ bị ảnh hưởng."}
        title={"Xóa người dùng"}
      />
    </div>
  );
};
